import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { MdImage, MdDelete, MdVisibility } from 'react-icons/md';
import './ImageManager.scss';

const PersonalisationImages = () => {
  const [images, setImages] = useState({
    first: null,
    second: null,
    third: null,
    fourth: null,
    fifth: null,
    sixth: null
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/personalisation/getImages');
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) {
      setError('Please select an image file first');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', type);

      const response = await axiosInstance.post('/personalisation/uploadImage', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage('Image uploaded successfully!');
      fetchImages(); // Refresh images
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imagePath, type) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.delete('/personalisation/deleteImage', {
        data: { imagePath }
      });
      
      setMessage('Image deleted successfully!');
      fetchImages(); // Refresh images
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setLoading(false);
    }
  };

  const ImageUploadSection = ({ title, description, type, currentImage }) => (
    <div className="image-section">
      <h3>{title}</h3>
      <p>{description}</p>
      
      <div className="file-input-container">
        <input
          type="file"
          id={`${type}-upload`}
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFileUpload(type, file);
            e.target.value = ''; // Reset input
          }}
          className="file-input"
        />
        <label htmlFor={`${type}-upload`} className="file-input-label">
          <MdImage />
          Upload Image
        </label>
      </div>

      <div className="current-images">
        {currentImage ? (
          <div className="single-image">
            <img src={`${SERVER}${currentImage}`} alt={type} />
            <div className="image-actions">
              <a href={`${SERVER}${currentImage}`} target="_blank" rel="noopener noreferrer">
                <MdVisibility />
              </a>
              <button onClick={() => handleDelete(currentImage, type)}>
                <MdDelete />
              </button>
            </div>
          </div>
        ) : (
          <p className="no-images">No image uploaded yet</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="image-manager-loading">
        <div className="loading-spinner"></div>
        <p>Loading images...</p>
      </div>
    );
  }

  return (
    <div className="image-manager">
      <div className="manager-header">
        <h2>Personalisation Page Images Management</h2>
        <p>Upload and manage images for the personalisation page</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          {message}
        </div>
      )}

      <div className="image-sections-grid">
        <ImageUploadSection
          title="First Image"
          description="First image in the personalisation section"
          type="first"
          currentImage={images.first}
        />

        <ImageUploadSection
          title="Second Image"
          description="Second image in the personalisation section"
          type="second"
          currentImage={images.second}
        />

        <ImageUploadSection
          title="Third Image"
          description="Third image in the personalisation section"
          type="third"
          currentImage={images.third}
        />

        <ImageUploadSection
          title="Fourth Image"
          description="Fourth image in the personalisation section"
          type="fourth"
          currentImage={images.fourth}
        />

        <ImageUploadSection
          title="Fifth Image"
          description="Fifth image in the personalisation section"
          type="fifth"
          currentImage={images.fifth}
        />

        <ImageUploadSection
          title="Sixth Image"
          description="Sixth image in the personalisation section"
          type="sixth"
          currentImage={images.sixth}
        />
      </div>
    </div>
  );
};

export default PersonalisationImages;